import { useEffect, useRef, useState } from "react";
import { useSessionsQuery } from "@/api/prompts";
import { useClaudeSession } from "@/hooks/useClaudeSession";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { NEW_PROJECT_KIND_KEY } from "@/components/template/NewMenu";
import { ImageToVectorDialog } from "@/components/prompt/ImageToVectorDialog";
import type { ProjectKind, Prompt } from "@/types/prompt";

/** Sidebar-panel icon (two vertical panes), used by both toggle buttons. */
function PanelIcon() {
  return (
    <svg
      className="h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      aria-hidden
    >
      <rect x="3" y="4" width="18" height="16" rx="2.5" />
      <path d="M9.5 4v16" />
    </svg>
  );
}

interface SessionSidebarProps {
  onSelectSession: (rootId: string) => void;
  onNewSession: () => void;
  activeSessionId: string | null;
}

/** Plus glyph for the collapsed-rail new-session trigger. */
function PlusIcon() {
  return (
    <svg
      className="h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden
    >
      <path strokeLinecap="round" d="M12 5v14m-7-7h14" />
    </svg>
  );
}

/**
 * New-session trigger + kind picker. Clicking opens a small popover offering
 * "Motion graphic" (Remotion) or "SVG graphic"; the choice is stashed as a
 * one-shot hint for the workspace to seed, then `onChoose` resets to a fresh
 * session. Rendered in both sidebar states via the `variant` prop.
 */
function NewSessionMenu({
  variant,
  onChoose,
  onImageToVector,
}: {
  variant: "full" | "icon";
  onChoose: (kind: ProjectKind) => void;
  onImageToVector: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const choose = (kind: ProjectKind) => {
    setOpen(false);
    onChoose(kind);
  };

  return (
    <div ref={ref} className={variant === "full" ? "relative flex-1" : "relative"}>
      {variant === "full" ? (
        <button
          onClick={() => setOpen((v) => !v)}
          className="w-full rounded-lg border border-line px-3 py-2 text-sm font-medium text-ink-2 transition-colors hover:bg-surface-3 hover:text-ink"
        >
          New session
        </button>
      ) : (
        <button
          onClick={() => setOpen((v) => !v)}
          title="New session"
          aria-label="New session"
          className="rounded-lg p-2 text-ink-3 transition-colors hover:bg-surface-3 hover:text-ink"
        >
          <PlusIcon />
        </button>
      )}
      {open && (
        <div
          className={`absolute z-40 min-w-[200px] overflow-hidden rounded-lg border border-line bg-surface py-1 shadow-lg ${
            variant === "icon" ? "left-full top-0 ml-1" : "left-0 top-full mt-1.5"
          }`}
        >
          <NewSessionChoice
            label="Motion graphic"
            hint="Animated Remotion video"
            onClick={() => choose("remotion")}
          />
          <NewSessionChoice
            label="SVG graphic"
            hint="Static vector image"
            onClick={() => choose("svg")}
          />
          <NewSessionChoice
            label="Image to vector"
            hint="Trace or pixel-art from an image"
            onClick={() => {
              setOpen(false);
              onImageToVector();
            }}
          />
        </div>
      )}
    </div>
  );
}

function NewSessionChoice({
  label,
  hint,
  onClick,
}: {
  label: string;
  hint: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="block w-full px-3 py-1.5 text-left transition-colors hover:bg-surface-3"
    >
      <div className="text-sm font-medium text-ink">{label}</div>
      <div className="text-xs text-ink-3">{hint}</div>
    </button>
  );
}

function relativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;

  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return "just now";

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;

  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function truncate(text: string, maxLen: number): string {
  if (text.length <= maxLen) return text;
  return text.slice(0, maxLen).trimEnd() + "...";
}

export function SessionSidebar({
  onSelectSession,
  onNewSession,
  activeSessionId,
}: SessionSidebarProps) {
  const { data: sessions, isLoading, isSuccess } = useSessionsQuery({ limit: 50 });
  const { cleanupOrphanedTranscripts } = useClaudeSession();
  // Collapsed state persists across visits — like ChatGPT's sidebar toggle.
  const [collapsed, setCollapsed] = useLocalStorage<boolean>(
    "motionforge-session-sidebar-collapsed",
    false,
  );
  // Image→vector dialog open-state is lifted here so a single instance serves
  // both the full and collapsed NewSessionMenu triggers.
  const [imageDialogOpen, setImageDialogOpen] = useState(false);

  // Rendered in both sidebar states. On success the dialog has already stashed
  // the handoff + kind hint, so we just start a fresh session like the kind
  // choices do — the workspace seeds itself from the handoff on mount.
  const imageDialog = (
    <ImageToVectorDialog
      open={imageDialogOpen}
      onClose={() => setImageDialogOpen(false)}
      onConverted={onNewSession}
    />
  );

  // Whenever the authoritative sessions list changes, GC any localStorage
  // transcripts for session ids the server no longer knows about. Fixes
  // the "I cleared my sessions from the DB but the Build tab still shows
  // old text" case — the DB is source of truth, and orphaned local blobs
  // shouldn't haunt the UI.
  useEffect(() => {
    if (!isSuccess) return;
    const knownIds = (sessions ?? []).map((s) => s.id);
    cleanupOrphanedTranscripts(knownIds, /* includeDraft */ knownIds.length === 0);
  }, [isSuccess, sessions, cleanupOrphanedTranscripts]);

  // Stash the chosen kind for the workspace to seed, then start fresh.
  const startNewSession = (kind: ProjectKind) => {
    try {
      sessionStorage.setItem(NEW_PROJECT_KIND_KEY, kind);
    } catch {
      // storage disabled — workspace falls back to remotion
    }
    onNewSession();
  };

  // Collapsed: a slim rail with expand + new-session icon buttons. The
  // session list itself only renders expanded, so nothing else changes.
  if (collapsed) {
    return (
      <div className="flex h-full w-12 min-w-12 flex-col items-center gap-1 border-r border-line bg-surface-2 py-3">
        <button
          onClick={() => setCollapsed(false)}
          title="Show session history"
          aria-label="Show session history"
          className="rounded-lg p-2 text-ink-3 transition-colors hover:bg-surface-3 hover:text-ink"
        >
          <PanelIcon />
        </button>
        <NewSessionMenu
          variant="icon"
          onChoose={startNewSession}
          onImageToVector={() => setImageDialogOpen(true)}
        />
        {imageDialog}
      </div>
    );
  }

  return (
    <div className="flex h-full w-[280px] min-w-[280px] flex-col border-r border-line bg-surface-2">
      {/* Header: New Session + collapse toggle */}
      <div className="flex items-center gap-2 border-b border-line p-3">
        <NewSessionMenu
          variant="full"
          onChoose={startNewSession}
          onImageToVector={() => setImageDialogOpen(true)}
        />
        <button
          onClick={() => setCollapsed(true)}
          title="Hide session history"
          aria-label="Hide session history"
          className="rounded-lg p-2 text-ink-3 transition-colors hover:bg-surface-3 hover:text-ink"
        >
          <PanelIcon />
        </button>
      </div>

      {/* Session list */}
      <div className="flex-1 overflow-y-auto p-2">
        <div className="mb-2 px-1 text-xs font-medium text-ink-3">
          Past sessions
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="h-3 w-3 animate-spin rounded-full border border-line-strong border-t-accent" />
          </div>
        ) : !sessions || sessions.length === 0 ? (
          <p className="px-1 py-4 text-xs text-ink-3">
            No sessions yet. Generate a template to start.
          </p>
        ) : (
          <div className="space-y-1">
            {sessions.map((session: Prompt) => (
              <SessionItem
                key={session.id}
                session={session}
                isActive={activeSessionId === session.id}
                onClick={() => onSelectSession(session.id)}
              />
            ))}
          </div>
        )}
      </div>
      {imageDialog}
    </div>
  );
}

function SessionItem({
  session,
  isActive,
  onClick,
}: {
  session: Prompt;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`block w-full rounded-lg px-3 py-2 text-left text-sm transition-colors ${
        isActive
          ? "bg-accent-soft text-accent"
          : "text-ink-2 hover:bg-surface-3 hover:text-ink"
      }`}
    >
      <div className="flex items-center gap-1.5">
        <span className="min-w-0 flex-1 truncate leading-snug">
          {truncate(session.prompt_text, 60)}
        </span>
        {session.project_kind === "svg" && (
          <span className="shrink-0 rounded bg-surface-3 px-1.5 py-0.5 text-[10px] font-medium text-ink-3">
            SVG
          </span>
        )}
      </div>
      <div className="mt-1 flex items-center gap-2">
        <span className="text-xs text-ink-3">
          {relativeTime(session.created_at)}
        </span>
      </div>
    </button>
  );
}
